import {Illustration} from '@atoms';
import {WorkspacesListWrapper} from './styles';
import {FlexCol, P, Span} from '@quarks';
import {useTranslation} from 'react-i18next';
import {HaNWorkspaceCell} from '@organisms';
import {AreaWithSensorInfo} from '@lib/store';
import {FloorListHeader} from '../FloorListHeader';
import {useHereAndNow} from '@pages';
import {useEffect, useRef} from 'react';
import {FloorSuggestionsTile} from '../FloorSuggestions/FloorSuggestionsTile';
import {trackEvent} from '@lib/infrastructure';

type HaNWorkspacesListProps = {
  onClick: (area: AreaWithSensorInfo) => void;
  selectedWorkspace: AreaWithSensorInfo | null;
};

export const HaNWorkspacesList = ({onClick, selectedWorkspace}: HaNWorkspacesListProps) => {
  const {t} = useTranslation();
  const {buildingId, tab, filterData, setHoverRoomId, selectedObject} = useHereAndNow();
  const areas = filterData.workspaces.filteredData;

  const indexOfElement = selectedWorkspace ? areas.indexOf(selectedWorkspace) : undefined;

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current
      ?.querySelector(`[data-han-list-item="${selectedObject?.id}"]`)
      ?.scrollIntoView({behavior: 'smooth', block: 'nearest'});
  }, [indexOfElement, scrollRef, selectedObject]);

  const {above, below} = filterData.workspaces.adjacentFilteredData;
  const aboveFloorId = above.at(0)?.floorId ?? null;
  const belowFloorId = below.at(0)?.floorId ?? null;

  return (
    <WorkspacesListWrapper ref={scrollRef}>
      <FloorListHeader type="available" />

      {areas.length !== 0 ? (
        <FlexCol
          rowGap={8}
          style={{padding: '8px 12px'}}>
          {areas.map((area) => (
            <Span
              key={area.id}
              data-han-list-item={area.id}
              onMouseEnter={() => setHoverRoomId(area.id)}
              onMouseLeave={() => setHoverRoomId(null)}>
              <HaNWorkspaceCell
                area={area}
                onClick={onClick}
              />
            </Span>
          ))}
        </FlexCol>
      ) : (
        <FlexCol
          alignItems="center"
          justifyContent="center"
          padding={32}
          flex="1"
          gap={16}>
          <Illustration illustration="noResults" />
          <P
            color="currentColor"
            textAlign="center">
            {t('hereAndNow:NoAreasAvailable')}
          </P>
        </FlexCol>
      )}

      {(aboveFloorId || belowFloorId) && (
        <>
          <FloorListHeader type="suggestion" />

          <FlexCol
            rowGap={8}
            style={{padding: '8px 12px'}}>
            {aboveFloorId && (
              <FloorSuggestionsTile
                floorId={aboveFloorId}
                direction={'up'}>
                {above.map((area) => (
                  <Span
                    key={area.id}
                    onMouseEnter={() => setHoverRoomId(area.id)}
                    onMouseLeave={() => setHoverRoomId(null)}>
                    <HaNWorkspaceCell
                      area={area}
                      onClick={(e) => {
                        onClick(e);
                        trackEvent('HereAndNow_OtherFloorSuggestions__SuggestionClick', {
                          buildingId,
                          tab,
                          floorId: aboveFloorId || '',
                          type: 'above',
                          nodeId: area.id,
                        });
                      }}
                    />
                  </Span>
                ))}
              </FloorSuggestionsTile>
            )}

            {belowFloorId && (
              <FloorSuggestionsTile
                floorId={belowFloorId}
                direction={'up'}>
                {below.map((area) => (
                  <Span
                    key={area.id}
                    onMouseEnter={() => setHoverRoomId(area.id)}
                    onMouseLeave={() => setHoverRoomId(null)}>
                    <HaNWorkspaceCell
                      area={area}
                      onClick={(e) => {
                        onClick(e);
                        trackEvent('HereAndNow_OtherFloorSuggestions__SuggestionClick', {
                          buildingId,
                          tab,
                          floorId: belowFloorId || '',
                          type: 'below',
                          nodeId: area.id,
                        });
                      }}
                    />
                  </Span>
                ))}
              </FloorSuggestionsTile>
            )}
          </FlexCol>
        </>
      )}
    </WorkspacesListWrapper>
  );
};
